import { useState, useEffect, useRef, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, MinusCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function timeFormat(dateStr) {
  const d = new Date(dateStr);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

export default function FloatingChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("pmw_guest_session") || "");
  const [guestName, setGuestName] = useState(() => localStorage.getItem("pmw_guest_name") || "");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [nameStep, setNameStep] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [unread, setUnread] = useState(0);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Create or resume session
  const startSession = useCallback(async (name) => {
    setLoading(true);
    try {
      const body = name ? { guest_name: name } : { session_id: sessionId };
      const res = await fetch(`${API_URL}/api/chat/guest/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const session = await res.json();
      setSessionId(session.id);
      setGuestName(session.guest_name);
      localStorage.setItem("pmw_guest_session", session.id);
      localStorage.setItem("pmw_guest_name", session.guest_name);
      // Load messages
      const msgRes = await fetch(`${API_URL}/api/chat/guest/messages/${session.id}`);
      const msgs = await msgRes.json();
      setMessages(msgs);
      // If new session (no messages), show AI greeting
      if (msgs.length === 0 && name) {
        setAiTyping(true);
        try {
          const greetRes = await fetch(`${API_URL}/api/chat/guest/messages/${session.id}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ content: `Hi, my name is ${name}` }),
          });
          const greetData = await greetRes.json();
          const newMsgs = [];
          if (greetData.guest_message) newMsgs.push(greetData.guest_message);
          if (greetData.ai_message) newMsgs.push(greetData.ai_message);
          setMessages(newMsgs);
        } catch {}
        setAiTyping(false);
      }
      setNameStep(false);
    } catch {
      // ignore
    }
    setLoading(false);
  }, [sessionId]);

  // On open, resume or prompt for name
  useEffect(() => {
    if (!open) return;
    if (sessionId) {
      startSession();
    } else {
      setNameStep(true);
    }
  }, [open, sessionId, startSession]);

  // WebSocket connection
  useEffect(() => {
    if (!open || !sessionId) return;
    const wsUrl = API_URL.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/guest-chat?session_id=${sessionId}`);
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.type === "admin_reply" && data.message) {
          setMessages((prev) => {
            if (prev.some((m) => m.id === data.message.id)) return prev;
            return [...prev, data.message];
          });
          setTimeout(scrollToBottom, 100);
        }
      } catch {}
    };

    const pingInterval = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 25000);

    return () => {
      clearInterval(pingInterval);
      ws.close();
    };
  }, [open, sessionId, scrollToBottom]);

  // Scroll on new messages
  useEffect(() => {
    if (open) setTimeout(scrollToBottom, 150);
  }, [messages, open, scrollToBottom]);

  // Track unread when closed
  useEffect(() => {
    if (!sessionId || open) return;
    const wsUrl = API_URL.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/ws/guest-chat?session_id=${sessionId}`);
    ws.onmessage = () => setUnread((prev) => prev + 1);
    const ping = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send("ping"); }, 25000);
    return () => { clearInterval(ping); ws.close(); };
  }, [sessionId, open]);

  const [aiTyping, setAiTyping] = useState(false);

  const sendMessage = async () => {
    if (!input.trim() || !sessionId) return;
    setSending(true);
    const content = input.trim();
    setInput("");
    // Optimistically add guest message
    const tempMsg = { id: "temp-" + Date.now(), session_id: sessionId, sender_type: "guest", sender_name: guestName, content, created_at: new Date().toISOString() };
    setMessages((prev) => [...prev, tempMsg]);
    setTimeout(scrollToBottom, 50);
    setAiTyping(true);
    try {
      const res = await fetch(`${API_URL}/api/chat/guest/messages/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content }),
      });
      const data = await res.json();
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempMsg.id);
        const newMsgs = [];
        if (data.guest_message) newMsgs.push(data.guest_message);
        if (data.ai_message) newMsgs.push(data.ai_message);
        return [...filtered, ...newMsgs];
      });
      setTimeout(scrollToBottom, 50);
    } catch {}
    setAiTyping(false);
    setSending(false);
    inputRef.current?.focus();
  };

  const handleNameSubmit = () => {
    const name = nameInput.trim() || "Visitor";
    startSession(name);
  };

  const handleOpen = () => {
    setOpen(true);
    setUnread(0);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={handleOpen}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-teal shadow-lg hover:bg-teal/90 transition-all hover:scale-105 flex items-center justify-center group"
          data-testid="floating-chat-btn"
          aria-label="Open live chat"
        >
          <MessageCircle className="w-6 h-6 text-white" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center" data-testid="chat-unread-badge">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          data-testid="floating-chat-window"
          style={{ animation: "slideUp 0.25s ease-out" }}
        >
          {/* Header */}
          <div className="bg-navy px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-teal/20 rounded-full flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-teal" />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">Live Support</p>
                <p className="text-slate-400 text-[11px]">We typically reply within minutes</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="minimize-chat-btn">
                <MinusCircle className="w-4 h-4 text-slate-400" />
              </button>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="close-chat-btn">
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Body */}
          {nameStep ? (
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center w-full">
                <div className="w-14 h-14 bg-teal/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="w-7 h-7 text-teal" />
                </div>
                <h3 className="text-navy font-semibold text-lg mb-1">Welcome!</h3>
                <p className="text-slate-500 text-sm mb-5">Enter your name to start chatting with our support team.</p>
                <input
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleNameSubmit()}
                  placeholder="Your name..."
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal mb-3"
                  autoFocus
                  data-testid="guest-name-input"
                />
                <button
                  onClick={handleNameSubmit}
                  disabled={loading}
                  className="w-full px-4 py-2.5 bg-teal text-white rounded-lg text-sm font-medium hover:bg-teal/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="start-chat-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start Chat"}
                </button>
              </div>
            </div>
          ) : loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-teal" />
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="guest-messages-area">
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-slate-400 text-sm">Send a message to start the conversation!</p>
                  </div>
                )}
                {messages.map((msg) => {
                  const isGuest = msg.sender_type === "guest";
                  return (
                    <div key={msg.id} className={`flex ${isGuest ? "justify-end" : "justify-start"}`} data-testid={`guest-msg-${msg.id}`}>
                      <div className={`max-w-[80%] ${isGuest ? "order-2" : ""}`}>
                        <div className={`flex items-center gap-1.5 mb-0.5 ${isGuest ? "justify-end" : ""}`}>
                          <span className="text-[11px] font-medium text-slate-500">{msg.is_ai ? "PMW Assistant" : msg.sender_name}</span>
                          <span className="text-[10px] text-slate-400">{timeFormat(msg.created_at)}</span>
                        </div>
                        <div className={`px-3 py-2 rounded-xl text-sm leading-relaxed ${isGuest ? "bg-teal text-white rounded-tr-sm" : "bg-slate-100 text-slate-700 rounded-tl-sm"}`}>
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {aiTyping && (
                  <div className="flex justify-start" data-testid="ai-typing-indicator">
                    <div className="max-w-[80%]">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-[11px] font-medium text-slate-500">PMW Assistant</span>
                      </div>
                      <div className="px-3 py-2 rounded-xl text-sm bg-slate-100 text-slate-500 rounded-tl-sm flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                        <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-slate-200 p-3 flex items-center gap-2 bg-white flex-shrink-0" data-testid="guest-input-area">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Type a message..."
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal/30 focus:border-teal"
                  data-testid="guest-message-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || sending}
                  className="w-9 h-9 bg-teal text-white rounded-lg flex items-center justify-center hover:bg-teal/90 transition-colors disabled:opacity-40"
                  data-testid="guest-send-btn"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
}
